import moment from 'moment';
import _ from 'lodash';

import { getState } from './App';
import { userIsAdmin } from './Auth';
import { getIntl } from './IntlInstance';
import { getUserSetting } from './Settings';

export const availableWidgets = [
  {
    id: 'recent-files',
    title: 'Recently added files',
    icon: 'fa-file',
  },
  {
    id: 'recent-collections',
    title: 'Recently updated Collections',
    icon: 'fa-layer-group',
  },
];

export const getDashboardAvailableComponents = () => {
  const state = getState();
  return state.App.settings.dashboard_available_components
    .filter((c) => {
      if (c.restrictions === 'admin') return userIsAdmin();
      return true;
    });
};

export const getDashboardComponentSettings = (slug) => {
  const componentSettings = getUserSetting('dashboard_widgets') || [];
  return componentSettings.find(c => c.slug === slug);
};

const getRegularMessages = (name) => {
  const intl = getIntl();

  return [
    intl.formatMessage({ defaultMessage: 'Jump back in, {name}!' }, { name }),
    intl.formatMessage({ defaultMessage: 'Let\'s do this, {name}!' }, { name }),
    intl.formatMessage({ defaultMessage: 'You\'re back! Let\'s go, {name}!' }, { name }),
    intl.formatMessage({ defaultMessage: 'Ready when you are, {name}!' }, { name }),
    intl.formatMessage({ defaultMessage: 'Back at it, {name}.' }, { name }),
    intl.formatMessage({ defaultMessage: 'What\'s up, {name}?' }, { name }),
    intl.formatMessage({ defaultMessage: 'Looking sharp today, {name}!' }, { name }),
    intl.formatMessage({ defaultMessage: 'Time to shine, {name}.' }, { name }),
    intl.formatMessage({ defaultMessage: 'Files at your service, {name}.' }, { name }),
    intl.formatMessage({ defaultMessage: 'Let\'s get things done, {name}.' }, { name }),
    intl.formatMessage({ defaultMessage: 'You\'ve got this, {name}!' }, { name }),
    intl.formatMessage({ defaultMessage: 'Welcome back, {name}.' }, { name }),
    intl.formatMessage({ defaultMessage: 'Where were we, {name}?' }, { name }),
    intl.formatMessage({ defaultMessage: 'Let\'s get organized, {name}.' }, { name }),
    intl.formatMessage({ defaultMessage: 'Your files missed you, {name}.' }, { name }),
    intl.formatMessage({ defaultMessage: 'Let\'s find those files, {name}.' }, { name }),
    intl.formatMessage({ defaultMessage: 'Let\'s find something awesome, {name}.' }, { name }),
    intl.formatMessage({ defaultMessage: 'Nice to see you again, {name}.' }, { name }),
    intl.formatMessage({ defaultMessage: 'Let\'s get to work, {name}.' }, { name }),
    intl.formatMessage({ defaultMessage: 'Hey, hey! You\'re back, {name}!' }, { name }),
    intl.formatMessage({ defaultMessage: 'Guess who\'s back? Back again.' }),
    intl.formatMessage({ defaultMessage: 'Plot twist: You\'re working today.' }),
    intl.formatMessage({ defaultMessage: 'Click stuff. Look busy.' }),
    intl.formatMessage({ defaultMessage: 'Today feels like a \'find stuff fast\' day.' }),
    intl.formatMessage({ defaultMessage: 'Let\'s organize like it\'s 1999.' }),
  ];
};

const getMondayMessages = (name) => {
  const intl = getIntl();

  return [
    intl.formatMessage({ defaultMessage: 'It\'s Monday — start strong, {name}!' }, { name }),
    intl.formatMessage({ defaultMessage: 'Monday? Again?' }),
    intl.formatMessage({ defaultMessage: 'New week, same files.' }),
    intl.formatMessage({ defaultMessage: 'The files aren\'t going to sort themselves.' }),
    intl.formatMessage({ defaultMessage: 'Start strong(ish), {name}!' }, { name }),
    intl.formatMessage({ defaultMessage: 'Still recovering from Sunday?' }),
    intl.formatMessage({ defaultMessage: 'Let\'s start... slowly.' }),
    intl.formatMessage({ defaultMessage: 'Back in the file saddle.' }),
    intl.formatMessage({ defaultMessage: 'The weekend was fun while it lasted.' }),
    intl.formatMessage({ defaultMessage: 'One file at a time, {name}.' }, { name }),
    intl.formatMessage({ defaultMessage: 'No sudden movements — it\'s Monday.' }),
    intl.formatMessage({ defaultMessage: 'It\'s Monday, file your feelings here.' }),
    intl.formatMessage({ defaultMessage: 'Logging in counts as productivity today, right?' }),
    intl.formatMessage({ defaultMessage: 'Reminder: coffee first, then everything else.' }),
  ];
};

const getWednesdayMessages = () => {
  const intl = getIntl();

  return [
    intl.formatMessage({ defaultMessage: 'It\'s Wednesday. We\'re halfway functional.' }),
    intl.formatMessage({ defaultMessage: 'Still going strong(ish).' }),
    intl.formatMessage({ defaultMessage: 'Keep calm and keep clicking.' }),
    intl.formatMessage({ defaultMessage: 'Files don\'t know it\'s Wednesday.' }),
  ];
};

const getFridayMessages = (name) => {
  const intl = getIntl();

  return [
    intl.formatMessage({ defaultMessage: 'It\'s Friday, almost free, {name}!' }, { name }),
    intl.formatMessage({ defaultMessage: 'Friday... one foot out the door?' }),
    intl.formatMessage({ defaultMessage: 'Let\'s fake focus for a few more hours.' }),
    intl.formatMessage({ defaultMessage: 'Finish strong. Or at least finish.' }),
    intl.formatMessage({ defaultMessage: 'Weekend loading...' }),
    intl.formatMessage({ defaultMessage: 'Let\'s wrap it up!' }),
    intl.formatMessage({ defaultMessage: 'Final lap — you got this.' }),
    intl.formatMessage({ defaultMessage: 'One last file for the road?' }),
    intl.formatMessage({ defaultMessage: 'File now, fun later.' }),
    intl.formatMessage({ defaultMessage: 'You made it. Kind of.' }),
    intl.formatMessage({ defaultMessage: 'Weekend mode: almost activated.' }),
  ];
};

const getWeekendMessages = () => {
  const intl = getIntl();

  return [
    intl.formatMessage({ defaultMessage: 'What are you doing here!? It\'s the weekend!' }),
    intl.formatMessage({ defaultMessage: 'Burning the weekend oil.' }),
    intl.formatMessage({ defaultMessage: 'Working on the weekend? Bold.' }),
    intl.formatMessage({ defaultMessage: 'Weekend mode: activated.' }),
    intl.formatMessage({ defaultMessage: 'Saturday is the new Monday?' }),
    intl.formatMessage({ defaultMessage: 'Look at you, getting things done!' }),
    intl.formatMessage({ defaultMessage: 'Weekend hustle = next-level dedication.' }),
    intl.formatMessage({ defaultMessage: 'Your files are surprised to see you.' }),
    intl.formatMessage({ defaultMessage: 'Bonus points for weekend productivity.' }),
    intl.formatMessage({ defaultMessage: 'We admire your commitment. From a distance.' }),
    intl.formatMessage({ defaultMessage: 'Weekend vibes: caffeine and clicking.' }),
    intl.formatMessage({ defaultMessage: 'You\'re either very dedicated or hiding from brunch.' }),
  ];
};

const getMorningMessages = (name) => {
  const intl = getIntl();

  return [
    intl.formatMessage({ defaultMessage: 'Good morning, {name}! Let\'s get started.' }, { name }),
    intl.formatMessage({ defaultMessage: 'Morning, {name}! Let\'s go.' }, { name }),
    intl.formatMessage({ defaultMessage: 'Files are up early — are you?' }),
    intl.formatMessage({ defaultMessage: 'Coffee ready? Time to work!' }),
    intl.formatMessage({ defaultMessage: 'Fresh day, fresh files.' }),
    intl.formatMessage({ defaultMessage: 'Early grind starts now!' }),
    intl.formatMessage({ defaultMessage: 'Sun\'s up, let\'s get moving!' }),
    intl.formatMessage({ defaultMessage: 'Mornings are for making progress.' }),
    intl.formatMessage({ defaultMessage: 'Let\'s start strong, {name}!' }, { name }),
    intl.formatMessage({ defaultMessage: 'Work waits for no one!' }),
    intl.formatMessage({ defaultMessage: 'Rise, shine, and work!' }),
  ];
};

const getAfternoonMessages = (name) => {
  const intl = getIntl();

  return [
    intl.formatMessage({ defaultMessage: 'Afternoon, {name}! Keep going.' }, { name }),
    intl.formatMessage({ defaultMessage: 'Midday check-in: need a file?' }),
    intl.formatMessage({ defaultMessage: 'Lunch done? Back to work!' }),
    intl.formatMessage({ defaultMessage: 'Afternoon hustle time!' }),
    intl.formatMessage({ defaultMessage: 'Sun\'s still up, let\'s go!' }),
    intl.formatMessage({ defaultMessage: 'Files don\'t nap — ready when you are!' }),
    intl.formatMessage({ defaultMessage: 'Let\'s power through the afternoon!' }),
    intl.formatMessage({ defaultMessage: 'Midday focus mode: ON.' }),
    intl.formatMessage({ defaultMessage: 'Still working? Your files are.' }),
  ];
};

const getEveningMessages = (name) => {
  const intl = getIntl();

  return [
    intl.formatMessage({ defaultMessage: 'Evening, {name}! Wrapping up?' }, { name }),
    intl.formatMessage({ defaultMessage: 'Sun\'s setting, but work\'s not!' }),
    intl.formatMessage({ defaultMessage: 'Evening shift activated.' }),
    intl.formatMessage({ defaultMessage: 'Still here? So are your files!' }),
    intl.formatMessage({ defaultMessage: 'Late work or early prep?' }),
    intl.formatMessage({ defaultMessage: 'Evening mode: ON.' }),
    intl.formatMessage({ defaultMessage: 'The day\'s almost done!' }),
    intl.formatMessage({ defaultMessage: 'One last push, {name}!' }, { name }),
    intl.formatMessage({ defaultMessage: 'Work now, relax later!' }),
    intl.formatMessage({ defaultMessage: 'You\'re working late, {name}.' }, { name }),
  ];
};

const getLateNightMessages = (name) => {
  const intl = getIntl();

  return [
    intl.formatMessage({ defaultMessage: 'Late night grind, {name}?' }, { name }),
    intl.formatMessage({ defaultMessage: 'Midnight hustle mode: ON.' }),
    intl.formatMessage({ defaultMessage: 'Files don\'t sleep — do you?' }),
    intl.formatMessage({ defaultMessage: 'Late hours, sharp focus.' }),
    intl.formatMessage({ defaultMessage: 'Dark mode, deep work.' }),
    intl.formatMessage({ defaultMessage: 'You and your files, night owls!' }),
    intl.formatMessage({ defaultMessage: 'It\'s quiet — perfect for work.' }),
    intl.formatMessage({ defaultMessage: 'Still up? Let\'s finish this!' }),
    intl.formatMessage({ defaultMessage: 'Sleep is optional, files aren\'t.' }),
    intl.formatMessage({ defaultMessage: 'Late but productive!' }),
    intl.formatMessage({ defaultMessage: 'You\'re working late, {name}.' }, { name }),
  ];
};

const seededRandom = (seed) => {
  let value = seed % 2147483647;
  return () => {
    value = (value * 16807) % 2147483647;
    return value / 2147483647;
  };
};

const getRandomItems = (array, currentDateNumber, userId, count) => {
  const seed = (currentDateNumber + userId) % 2147483647;
  const random = seededRandom(seed);

  const shuffledArray = _.orderBy(array, () => random());

  return shuffledArray.slice(0, count);
};

const getTimeOfDayMessages = (name) => {
  const hour = moment().hour();

  if (hour >= 5 && hour < 12) {
    return getMorningMessages(name); // Morning, 5 AM - 11 AM
  } if (hour >= 12 && hour < 18) {
    return getAfternoonMessages(name); // Afternoon, 12 PM - 5 PM
  } if (hour >= 18 && hour < 23) {
    return getEveningMessages(name); // Evening, 6 PM - 10 PM
  }
  return getLateNightMessages(name); // Late night, 11 PM - 4 AM
};

export const getWelcomeMessage = (name, uuid = '1') => {
  const numbers = parseInt(uuid.replace(/\D/g, '')) || 1;
  const dateNumber = parseInt(moment().format('DDMMYYYY'));

  let messages = [];

  const day = moment().day();

  if (day === 1) {
    messages.push(getRandomItems(getMondayMessages(name), dateNumber, numbers, 3));
  }

  if (day === 3) {
    messages.push(getRandomItems(getWednesdayMessages(name), dateNumber, numbers, 3));
  }

  if (day === 5) {
    messages.push(getRandomItems(getFridayMessages(name), dateNumber, numbers, 3));
  }

  if (day === 6 || day === 0) {
    messages.push(getRandomItems(getWeekendMessages(name), dateNumber, numbers, 3));
  }

  messages.push(getRandomItems(getRegularMessages(name), dateNumber, numbers, 6 - messages.length));

  messages.push(getRandomItems(getTimeOfDayMessages(name), dateNumber, numbers, 2));

  messages = messages.flat();

  const messageIndex = _.random(0, messages.length - 1);

  return messages[messageIndex];
};
